import { useEffect, useState } from 'react';
import { Rnd } from 'react-rnd';
import { useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';
import { useWindowSize } from 'usehooks-ts';
import { DEFAULT_ASPECT_RATIO, DEFAULT_RIGHT_CARD_WIDTH } from '../../../constants/app';
import { useStyles } from '../../../hooks/useStyles';
import { rigthCardWidthState, sidenavWidthState } from '../../../storage/app';
import { RightCard } from '../../../types/app';

type Props = Readonly<{
  children: React.ReactNode;
  name: RightCard;
  defaultWidth?: number;
  initialScale?: number;
  rows?: number;
  safeBottomPadding?: number;
}>;

function FloatingRightCard({
  children,
  name,
  defaultWidth = DEFAULT_RIGHT_CARD_WIDTH,
  initialScale = 1,
  rows = 1,
  safeBottomPadding = 220,
}: Props) {
  const { css, theme } = useStyles();
  const { height, width } = useWindowSize();
  const sidenavWidth = useRecoilValue(sidenavWidthState(false));
  const initialWindowWidth = defaultWidth * initialScale;
  const gaps = 16 * (rows - 1);
  const defaultHeigth = initialWindowWidth / DEFAULT_ASPECT_RATIO;
  const initialWindowHeight = defaultHeigth * rows + gaps;
  const [windowWidth, setWindowWidth] = useState(initialWindowWidth);
  const setCardWidth = useSetRecoilState(rigthCardWidthState(name));
  const resetCardWidth = useResetRecoilState(rigthCardWidthState(name));

  useEffect(() => {
    setCardWidth(windowWidth);
  }, [windowWidth]);

  useEffect(() => {
    return () => {
      resetCardWidth();
    };
  }, []);

  return (
    <Rnd
      lockAspectRatio
      default={{
        x: width - (initialWindowWidth + sidenavWidth + 12),
        y: 60,
        width: initialWindowWidth,
        height: initialWindowHeight,
      }}
      maxWidth="40%"
      minWidth={`${defaultWidth}px`}
      maxHeight={height - safeBottomPadding}
      onResizeStop={(_e, _dir, elementRef) => {
        setWindowWidth(elementRef.offsetWidth);
      }}
      cancel=".player"
      className={css({
        backgroundColor: '#fff',
        border: '1px solid #E2E2E2',
        borderRadius: theme.sizing.scale500,
        display: 'flex',
        overflow: 'hidden',
        padding: theme.sizing.scale600,
      })}
      enableResizing={{
        topLeft: false,
        topRight: false,
        bottomLeft: true,
        bottomRight: false,
      }}
      bounds="body"
      disableDragging
      position={{
        x: width - (windowWidth + sidenavWidth + 12),
        y: 60,
      }}
    >
      {children}
    </Rnd>
  );
}

export default FloatingRightCard;
